import React, { useEffect, useState, useRef } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle, Mail, Search } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";

const MERGE_TAGS = [
  { label: "Contact Name", token: "{{contact_name}}" },
  { label: "Company Name", token: "{{company_name}}" },
  { label: "Company ID", token: "{{company_id}}" },
  { label: "Recent Job Title", token: "{{recent_job_title}}" },
  { label: "Job Posted Date", token: "{{job_posted_date}}" },
  { label: "City", token: "{{city}}" },
  { label: "Salary", token: "{{salary}}" },
  { label: "Applicants Count", token: "{{applicants_count}}" },
  { label: "Freshers Allowed", token: "{{freshers_allowed}}" },
  { label: "Coupon Code", token: "{{coupon_code}}" },
  { label: "Coupon Terms", token: "{{coupon_terms}}" },
  { label: "Job Link", token: "{{job_link}}" },
  { label: "Today's Date", token: "{{today}}" },
];

const MODE_CONFIG = {
  newfeed: { title: "New Feed CRM", subtitle: "Companies as they come in — a lean view for quick first-touch outreach.", channels: ["whatsapp"] },
  coupon: { title: "Coupon Code CRM", subtitle: "Send or resend coupon codes to companies.", channels: ["whatsapp"] },
  normal: { title: "Normal CRM", subtitle: "The full outreach view — WhatsApp and email, every tag available.", channels: ["whatsapp", "email"] },
};

function isRecent(iso) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 60000;
}

export default function CrmPanel({ mode }) {
  const config = MODE_CONFIG[mode];
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [sort, setSort] = useState("newest");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeTarget, setComposeTarget] = useState(null);
  const [message, setMessage] = useState("");
  const [channel, setChannel] = useState(config.channels[0]);
  const textareaRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/crm/companies", { params: { q, city, sort, page, limit: 25 } })
      .then(r => { setCompanies(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, city, sort, page]);
  useEffect(() => { api.get("/cities").then(r => setCities(r.data)); }, []);

  const openCompose = (c) => {
    setComposeTarget(c);
    setMessage("");
    setChannel(config.channels[0]);
  };

  const insertTagAtCursor = (token) => {
    const el = textareaRef.current;
    if (!el) { setMessage(m => m + token); return; }
    const start = el.selectionStart ?? message.length;
    const end = el.selectionEnd ?? message.length;
    const next = message.slice(0, start) + token + message.slice(end);
    setMessage(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const token = e.dataTransfer.getData("text/plain");
    if (!token) return;
    insertTagAtCursor(token);
  };

  const resolveTagsForCompany = (c) => {
    const coupon = (c.coupons || [])[0];
    const post = c.recent_post;
    let salary = "—";
    if (post) {
      if (post.salary_type === "hidden" || !post.show_salary) salary = "Not disclosed";
      else if (post.salary_type === "yearly") salary = `${post.salary_min ?? 0}–${post.salary_max ?? 0} LPA`;
      else salary = `₹${(post.salary_min ?? 0).toLocaleString()}–₹${(post.salary_max ?? 0).toLocaleString()}/month`;
    }
    const freshers = post?.fresher_option === "freshers_only" ? "Only freshers"
      : post?.fresher_option === "freshers_can_apply" ? "Freshers can apply" : "—";
    return {
      "{{contact_name}}": c.name,
      "{{company_name}}": c.name,
      "{{company_id}}": c.company_id,
      "{{recent_job_title}}": post?.title || "—",
      "{{job_posted_date}}": (post?.created_at || "").slice(0, 10) || "—",
      "{{city}}": c.city || "—",
      "{{salary}}": salary,
      "{{applicants_count}}": post ? String(post.applications_count ?? 0) : "—",
      "{{freshers_allowed}}": freshers,
      "{{coupon_code}}": coupon?.code || "—",
      "{{coupon_terms}}": coupon
        ? `${coupon.discount_value}% off, ${Math.max(0, (coupon.max_uses || 0) - (coupon.used_count || 0))} of ${coupon.max_uses} uses left`
        : "—",
      "{{job_link}}": post ? `${window.location.origin}/jobs/${post.id}` : "—",
      "{{today}}": new Date().toISOString().slice(0, 10),
    };
  };

  const previewMessage = () => {
    if (!composeTarget) return message;
    const tagMap = resolveTagsForCompany(composeTarget);
    let out = message;
    Object.entries(tagMap).forEach(([token, val]) => { out = out.split(token).join(val); });
    return out;
  };

  const send = async () => {
    try {
      const finalMsg = previewMessage();
      const { data } = await api.post("/admin/crm/send", {
        company_id: composeTarget.company_id, message: finalMsg, channel,
      });
      window.open(data.link, "_blank");
      toast.success(`Logged — opening ${channel === "email" ? "your email client" : "WhatsApp"}...`);
      setComposeTarget(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid={`crm-${mode}`}>
      <h2 className="text-2xl font-bold mb-1">{config.title}</h2>
      <p className="text-slate-500 text-sm mb-6">{config.subtitle}</p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search company name or ID..." value={q} onChange={e => { setPage(1); setQ(e.target.value); }} className="pl-9" />
        </div>
        <div className="w-48">
          <SearchableSelect
            value={city || "all"}
            onChange={v => { setPage(1); setCity(v === "all" ? "" : v); }}
            clearValue="all"
            clearLabel="All cities"
            searchPlaceholder="Search city..."
            options={cities.map(c => ({ value: c.name, label: c.name }))}
            triggerClassName="h-10 px-3 rounded-md border border-slate-200 bg-white"
          />
        </div>
        <select value={sort} onChange={e => setSort(e.target.value)} className="border border-slate-200 rounded-md px-3 h-10 text-sm bg-white">
          <option value="newest">Newest first</option>
          <option value="az">Name A–Z</option>
          <option value="id">Company ID</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Recent Post</th>
              <th className="text-left px-4 py-3">First Free</th>
              <th className="text-left px-4 py-3">City</th>
              <th className="text-left px-4 py-3">Last Sent</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading...</td></tr>
            ) : companies.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No companies found</td></tr>
            ) : companies.map((c, i) => (
              <tr key={c.company_id}>
                <td className="px-4 py-3 text-slate-400">{(page - 1) * 25 + i + 1}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-400 font-mono">{c.company_id}</div>
                </td>
                <td className="px-4 py-3">
                  {c.recent_post ? (
                    <>
                      <div className="text-slate-700">{c.recent_post.title}</div>
                      <div className="text-xs text-slate-400">{(c.recent_post.created_at || "").slice(0, 10)}</div>
                    </>
                  ) : <span className="text-slate-400">No posts yet</span>}
                </td>
                <td className="px-4 py-3">
                  {c.claimed_first_free
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">Used</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Available</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">{c.city}</td>
                <td className="px-4 py-3">
                  {c.last_sent ? (
                    <div className="text-xs text-slate-500">
                      <div>{isRecent(c.last_sent.sent_at) ? "Sent just now" : (c.last_sent.sent_at || "").slice(0, 16).replace("T", " ")}</div>
                      <div className="text-slate-400">by {c.last_sent.sent_by_name}</div>
                    </div>
                  ) : <span className="text-slate-400 text-xs">Never</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => openCompose(c)} data-testid={`crm-message-${c.company_id}`}>
                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> Message
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center gap-2 mt-4">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
        <span className="px-3 py-1.5 text-sm text-slate-500">Page {page} of {Math.max(1, Math.ceil(total / 25))}</span>
        <Button size="sm" variant="outline" disabled={page * 25 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
      </div>

      <Dialog open={!!composeTarget} onOpenChange={(o) => !o && setComposeTarget(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Message {composeTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {config.channels.length > 1 && (
              <div className="flex gap-2">
                {config.channels.map(ch => (
                  <button
                    key={ch}
                    type="button"
                    onClick={() => setChannel(ch)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium capitalize flex items-center gap-1 ${
                      channel === ch ? "bg-black text-white border-black" : "border-slate-200 text-slate-600"
                    }`}
                  >
                    {ch === "whatsapp" ? <MessageCircle className="w-3 h-3" /> : <Mail className="w-3 h-3" />} {ch}
                  </button>
                ))}
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1.5">Drag a tag into the message, or click to insert at cursor:</p>
              <div className="flex flex-wrap gap-1.5">
                {MERGE_TAGS.map(t => (
                  <span
                    key={t.token}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", t.token)}
                    onClick={() => insertTagAtCursor(t.token)}
                    className="text-[11px] font-medium px-2 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200 cursor-grab active:cursor-grabbing hover:bg-slate-200 select-none"
                    data-testid={`crm-tag-${t.token}`}
                  >
                    {t.label}
                  </span>
                ))}
              </div>
            </div>
            <Textarea
              ref={textareaRef}
              rows={6}
              value={message}
              onChange={e => setMessage(e.target.value)}
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              placeholder="Type your message, or drag tags in above..."
              data-testid="crm-message-input"
            />
            {message && (
              <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
                <span className="font-medium">Preview: </span>{previewMessage()}
              </div>
            )}
            <Button onClick={send} disabled={!message.trim()} className="w-full bg-black hover:bg-slate-800 text-white" data-testid="crm-send-btn">
              {channel === "email" ? <Mail className="w-4 h-4 mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
              Send via {channel === "email" ? "Email" : "WhatsApp"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
